import React, { useEffect, useState } from "react";
import Breadcrumb from "../../../../layouts/full-layout/breadcrumb/Breadcrumb";
import FeatherIcon from "feather-icons-react";
import {
  AddCandidateCV,
  GetCandidateCV,
  SendCandidateFeedback,
} from "../../../../services/human-resource";

import { useTheme } from "@mui/material/styles";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";

import {
  Card,
  CardContent,
  TableHead,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  TableFooter,
  IconButton,
  TableSortLabel,
  TableContainer,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  MenuItem,
  TextField,
  CircularProgress,
} from "@mui/material";

import { toast } from "react-toastify";
import { Form, FormikProvider, useFormik } from "formik";
import PropTypes from "prop-types";

import CVValidation from "../../../../validationSchema/AddCV";
import { AddCV } from "../AddResume/AddCV";
import { getAddNewResumeObj } from "../AddResume/EmptyAddResume";
import { enums } from "../../../../enums/Enums";
import { findCurrectPermission } from "../../../../helpers/roles-and-permissions";

const BCrumb = [
  {
    to: "/",
    title: "HR Management",
  },
  {
    title: "All Candidate CV",
  },
];

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const totalPages = Math.ceil(count / rowsPerPage);

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, totalPages - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="First page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>

      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="Previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>

      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= totalPages - 1}
        aria-label="Next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>

      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= totalPages - 1}
        aria-label="Last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const AllCandidateCV = () => {
  const [candidateCV, setCandidateCV] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  const [orderDirection, setOrderDirection] = useState("desc");
  const [valueToOrderBy, setValueToOrderBy] = useState("ID");

  const [totalRecords, setTotalRecords] = useState(0);
  const [searchValue] = useState("");

  const [open, setOpen] = useState(false);
  const [selectCandidate] = useState(getAddNewResumeObj());
  const [addCVPermission, setAddCVPermission] = useState(false);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const [feedbackForm, setFeedbackForm] = useState({
    interested: "Yes",
    interviewDate: "",
    comments: "",
  });

  useEffect(() => {
    const permission = findCurrectPermission(
      enums.UserPermissionNames.HR_AddCandidateResume
    );

    setAddCVPermission(permission);
  }, []);

  const formik = useFormik({
    initialValues: selectCandidate,
    validationSchema: CVValidation,

    onSubmit: (values, { resetForm }) => {
      addResume(values, resetForm);
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const addResume = async (values, resetForm) => {
    const formData = new FormData();

    formData.append("FirstName", values.firstName || "");
    formData.append("MiddleName", values.middleName || "");
    formData.append("LastName", values.lastName || "");
    formData.append("Education", values.education || "");
    formData.append("Experience", values.experience || "");
    formData.append("Contact", values.contact || "");
    formData.append("Remarks", values.remarks || "");

    if (values.cv) {
      formData.append("Attachment", values.cv);
    }

    try {
      const response = await AddCandidateCV(formData);

      if (response?.status === 200) {
        toast.success("Candidate added successfully");

        resetForm();
        setOpen(false);
        getCandidateCV();
      }
    } catch (error) {
      console.error("Add candidate error:", error);

      toast.error(
        error?.response?.data?.message || "Unable to add candidate"
      );
    }
  };

  const getCandidateCV = async () => {
    const data = {
      start: rowsPerPage * page,
      length: rowsPerPage,
      sortExpression: `${valueToOrderBy} ${orderDirection}`,
      searchKeyword: searchValue?.trim() || null,
    };

    try {
      const response = await GetCandidateCV(data);

      if (response?.data) {
        setCandidateCV(response?.data?.result || []);
        setTotalRecords(response?.data?.totalRecords || 0);
      }
    } catch (error) {
      console.error("Get candidate CV error:", error);

      setCandidateCV([]);
      setTotalRecords(0);

      toast.error(
        error?.response?.data?.message ||
        "Unable to load candidate resumes"
      );
    }
  };

  useEffect(() => {
    getCandidateCV();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    valueToOrderBy,
    orderDirection,
    searchValue,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const selectedRows = parseInt(event.target.value, 10);

    setRowsPerPage(selectedRows);
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAscending =
      valueToOrderBy === property && orderDirection === "asc";

    setValueToOrderBy(property);
    setOrderDirection(isAscending ? "desc" : "asc");
    setPage(0);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "-";
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const getFileName = (path) => {
    if (!path) {
      return "-";
    }

    try {
      const cleanPath = path.split("?")[0];
      return decodeURIComponent(cleanPath.split("/").pop());
    } catch {
      return path;
    }
  };

  const showValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    return value;
  };

  const handleFeedbackOpen = (candidate) => {
    setSelectedCandidate(candidate);

    setFeedbackForm({
      interested: "Yes",
      interviewDate: "",
      comments: "",
    });

    setFeedbackOpen(true);
  };

  const handleFeedbackClose = () => {
    if (feedbackLoading) {
      return;
    }

    setFeedbackOpen(false);
    setSelectedCandidate(null);

    setFeedbackForm({
      interested: "Yes",
      interviewDate: "",
      comments: "",
    });
  };

  const handleFeedbackChange = (event) => {
    const { name, value } = event.target;

    setFeedbackForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleFeedbackSubmit = async () => {
    if (!selectedCandidate?.id) {
      toast.error("Candidate information is not available");
      return;
    }

    if (!feedbackForm.interested) {
      toast.error("Please select interested status");
      return;
    }

    if (
      feedbackForm.interested === "Yes" &&
      !feedbackForm.interviewDate
    ) {
      toast.error("Please select interview date");
      return;
    }

    if (!feedbackForm.comments.trim()) {
      toast.error("Please enter comments");
      return;
    }

    const requestData = {
      candidateId: selectedCandidate.id,

      interestedStatus: feedbackForm.interested,
      
      candidateName:
        selectedCandidate.personName || "",

      interviewDate:
        feedbackForm.interested === "Yes"
          ? feedbackForm.interviewDate
          : null,

      comments: feedbackForm.comments.trim(),
    };

    try {
      setFeedbackLoading(true);

      const response = await SendCandidateFeedback(requestData);

      if (response?.status === 200) {
        toast.success("Feedback submitted and email sent to HR");

        setFeedbackOpen(false);
        setSelectedCandidate(null);

        setFeedbackForm({
          interested: "Yes",
          interviewDate: "",
          comments: "",
        });
      }
    } catch (error) {
      console.error("Candidate feedback error:", error);

      toast.error(
        error?.response?.data?.message ||
        "Unable to submit candidate feedback"
      );
    } finally {
      setFeedbackLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb title="All Candidate CV" items={BCrumb} />

      <Card sx={{ p: 0 }}>
        <Box
          display="flex"
          alignItems="center"
          p={2}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="file-text" width="18" />

          <Box sx={{ ml: 1 }}>
            All Candidate CV
          </Box>

          {addCVPermission && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickOpen}
              sx={{ ml: "auto" }}
            >
              Add Candidate CV
            </Button>
          )}
        </Box>

        <CardContent>
          <Grid item xs={12} sx={{ overflow: "auto" }}>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 550,
                width: "100%",
                overflowX: "auto",
              }}
            >
              <Table
                stickyHeader
                aria-label="Candidate resumes table"
                sx={{
                  minWidth: 1250,
                  tableLayout: "fixed",
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* ID */}
                    <TableCell>
                      <TableSortLabel
                        active={valueToOrderBy === "ID"}
                        direction={
                          valueToOrderBy === "ID"
                            ? orderDirection
                            : "asc"
                        }
                        onClick={() => handleRequestSort("ID")}
                      >
                        ID
                      </TableSortLabel>
                    </TableCell>

                    {/* Full name */}
                    <TableCell>
                      <TableSortLabel
                        active={valueToOrderBy === "PersonName"}
                        direction={
                          valueToOrderBy === "PersonName"
                            ? orderDirection
                            : "asc"
                        }
                        onClick={() => handleRequestSort("PersonName")}
                      >
                        Person Name
                      </TableSortLabel>
                    </TableCell>

                    {/* Education */}
                    <TableCell>
                      <TableSortLabel
                        active={valueToOrderBy === "Education"}
                        direction={
                          valueToOrderBy === "Education"
                            ? orderDirection
                            : "asc"
                        }
                        onClick={() => handleRequestSort("Education")}
                      >
                        Education
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>Experience</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Remarks</TableCell>

                    {/* Created date */}
                    <TableCell>
                      <TableSortLabel
                        active={valueToOrderBy === "CreatedAt"}
                        direction={
                          valueToOrderBy === "CreatedAt"
                            ? orderDirection
                            : "asc"
                        }
                        onClick={() => handleRequestSort("CreatedAt")}
                      >
                        Created At
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel
                        active={valueToOrderBy === "CreatedByName"}
                        direction={
                          valueToOrderBy === "CreatedByName"
                            ? orderDirection
                            : "asc"
                        }
                        onClick={() => handleRequestSort("CreatedByName")}
                      >
                        Created By
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {candidateCV.length > 0 ? (
                    candidateCV.map((row) => (
                      <TableRow
                        key={row?.id}
                        hover
                      >
                        <TableCell>
                          {showValue(row?.id)}
                        </TableCell>

                        <TableCell
                          sx={{
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            minWidth: 180,
                            maxWidth: 250,
                          }}
                        >
                          {showValue(row?.personName)}
                        </TableCell>

                        <TableCell
                          sx={{
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            minWidth: 180,
                            maxWidth: 250,
                          }}
                        >
                          {showValue(row?.education)}
                        </TableCell>

                        <TableCell
                          sx={{
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            minWidth: 200,
                            maxWidth: 300,
                          }}
                        >
                          {showValue(row?.experience)}
                        </TableCell>

                        <TableCell>
                          {showValue(row?.contact)}
                        </TableCell>

                        <TableCell
                          sx={{
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            minWidth: 200,
                            maxWidth: 350,
                          }}
                        >
                          {showValue(row?.remarks)}
                        </TableCell>

                        <TableCell>
                          {formatDate(row?.createdAt)}
                        </TableCell>

                        <TableCell>
                          {showValue(row?.createdByName)}
                        </TableCell>

                        <TableCell align="center">
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            gap={0.5}
                          >
                            {(row?.cvPath || row?.cv) && (
                              <>
                                <Tooltip title="View CV">
                                  <IconButton
                                    component="a"
                                    href={row?.cvPath || row?.cv}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    color="primary"
                                    size="small"
                                  >
                                    <RemoveRedEyeOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Download CV">
                                  <IconButton
                                    component="a"
                                    href={row?.cvPath || row?.cv}
                                    download={getFileName(row?.cvPath || row?.cv)}
                                    color="primary"
                                    size="small"
                                  >
                                    <FileDownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}

                            <Tooltip title="Add Candidate Feedback">
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => handleFeedbackOpen(row)}
                                sx={{
                                  textTransform: "none",
                                  whiteSpace: "nowrap",
                                  minWidth: 95,
                                }}
                              >
                                Feedback
                              </Button>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={14}
                        align="center"
                        sx={{ py: 5 }}
                      >
                        No candidate resume found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

                <TableFooter>
                  <TableRow />
                </TableFooter>
              </Table>
            </TableContainer>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              overflowX: "auto",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[
                5,
                10,
                25,
                ...(totalRecords > 0
                  ? [
                    {
                      label: "All",
                      value: totalRecords,
                    },
                  ]
                  : []),
              ]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  "aria-label": "Rows per page",
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          Add Candidate CV
        </DialogTitle>

        <DialogContent>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              <Box sx={{ width: "100%", typography: "body1" }}>
                <AddCV />
              </Box>
            </Form>
          </FormikProvider>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>

          <Button
            onClick={formik.handleSubmit}
            color="primary"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={feedbackOpen}
        onClose={handleFeedbackClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Candidate Feedback
        </DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 1,
            }}
          >
            <TextField
              label="Candidate"
              value={selectedCandidate?.personName || ""}
              fullWidth
              disabled
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              <TextField
                select
                label="Interested?"
                name="interested"
                value={feedbackForm.interested}
                onChange={handleFeedbackChange}
                fullWidth
                required
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
                <MenuItem value="Hold">Hold</MenuItem>
              </TextField>

              <TextField
                label="Interview Date"
                name="interviewDate"
                type="date"
                value={feedbackForm.interviewDate}
                onChange={handleFeedbackChange}
                fullWidth
                required={feedbackForm.interested === "Yes"}
                disabled={feedbackForm.interested !== "Yes"}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>

            <TextField
              label="Comments"
              name="comments"
              value={feedbackForm.comments}
              onChange={handleFeedbackChange}
              placeholder="Add manager feedback..."
              multiline
              rows={4}
              fullWidth
              required
              inputProps={{
                maxLength: 1000,
              }}
              helperText={`${feedbackForm.comments.length}/1000`}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleFeedbackClose}
            disabled={feedbackLoading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleFeedbackSubmit}
            disabled={feedbackLoading}
            startIcon={
              feedbackLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {feedbackLoading ? "Sending..." : "Submit Feedback"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AllCandidateCV;